import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    CircularProgress,
    InputAdornment,
    IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import cuentaCorrienteProveedoresService, { CuentaCorrienteProveedorSaldo } from '../../services/cuentaCorrienteProveedoresService';
import { formatCurrency } from '../../utils/formatters';

interface ProveedorSaldoSearchProps {
    open: boolean;
    onClose: () => void;
    onSelect: (proveedor: CuentaCorrienteProveedorSaldo) => void;
}

const ProveedorSaldoSearch: React.FC<ProveedorSaldoSearchProps> = ({ open, onClose, onSelect }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchTerm, setSearchTerm] = useState('');
    const [proveedores, setProveedores] = useState<CuentaCorrienteProveedorSaldo[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSearch = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const trimmed = searchTerm.trim();
            const compact = trimmed.replace(/\s+/g, '');
            
            // Cargar todos los proveedores con saldo
            const response = await cuentaCorrienteProveedoresService.getSaldos({
                pageNumber: 1,
                pageSize: 10000
            });

            let filtered = response.items || [];

            // Si hay término de búsqueda, filtrar
            if (compact !== '') {
                // Si es numérico puro, buscar por nroProveedor
                if (/^\d+$/.test(compact)) {
                    filtered = filtered.filter(
                        (p) => p.nroProveedor.toString().includes(compact)
                    );
                } else {
                    // Buscar por nombre
                    const searchLower = trimmed.toLowerCase();
                    filtered = filtered.filter(
                        (p) => p.nombre.toLowerCase().includes(searchLower)
                    );
                }
            }

            setProveedores(filtered);
            if (filtered.length === 0) {
                setError('No se encontraron proveedores.');
            }
        } catch (err: any) {
            setError(`Error al buscar proveedores: ${err.message || 'Error desconocido'}`);
            setProveedores([]);
        } finally {
            setLoading(false);
        }
    }, [searchTerm]);

    const handleSelect = (proveedor: CuentaCorrienteProveedorSaldo) => {
        onSelect(proveedor);
        onClose();
    };

    const handleClose = () => {
        setSearchTerm('');
        setProveedores([]);
        setError(null);
        onClose();
    };

    // Búsqueda automática con debounce cuando está abierto
    useEffect(() => {
        if (!open) {
            setSearchTerm('');
            setProveedores([]);
            setError(null);
            return;
        }
        const handle = setTimeout(() => {
            handleSearch();
        }, 400);
        return () => clearTimeout(handle);
    }, [open, searchTerm, handleSearch]);

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isSmallScreen}
            sx={{
                '& .MuiDialog-paper': {
                    margin: { xs: 0, sm: 2 },
                    maxHeight: { xs: '100%', sm: '90vh' },
                    height: { xs: '100%', sm: 'auto' }
                }
            }}
        >
            <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                        Buscar Proveedor con Saldo
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            
            <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Número de proveedor o nombre"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleSearch} disabled={loading} edge="end">
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        sx={{ mb: 1.5 }}
                    />
                </Box>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {error && !loading && (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                )}

                {!loading && proveedores.length > 0 && (
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Nro. Proveedor</TableCell>
                                    <TableCell>Nombre</TableCell>
                                    <TableCell align="right">Saldo Total</TableCell>
                                    <TableCell align="right">Saldo Vigente</TableCell>
                                    <TableCell align="right">Saldo Vencido</TableCell>
                                    <TableCell>Acción</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {proveedores.map((proveedor) => (
                                    <TableRow key={proveedor.nroProveedor} hover>
                                        <TableCell>{proveedor.nroProveedor}</TableCell>
                                        <TableCell>{proveedor.nombre}</TableCell>
                                        <TableCell align="right">{formatCurrency(proveedor.saldoTotal)}</TableCell>
                                        <TableCell align="right">{formatCurrency(proveedor.saldoVigente)}</TableCell>
                                        <TableCell align="right">{formatCurrency(proveedor.saldoVencido)}</TableCell>
                                        <TableCell>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={() => handleSelect(proveedor)}
                                            >
                                                Seleccionar
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ProveedorSaldoSearch;




